import {
  SEAT_NAME_ABSOLUTE_MIN_PX,
  SEAT_NAME_MIN_PAINTED_PX,
  SEAT_STACK_MIN_FONT_PX,
  SEAT_STACK_MIN_PAINTED_PX,
  SEAT_TEXT_MIN_FONT_PX
} from 'constants/gameConstants';
import { useAnimatedWinStack } from 'hooks/useAnimatedWinStack';
import { useDisplayedStack } from 'hooks/useDisplayedStack';
import { useTableSelector } from 'hooks/useTableSelector';
import { tableMoneyFormatterCompact } from 'utils/functions';

import { useShrinkToFit } from 'components/Seat/hooks/useShrinkToFit';
import { SeatWithPlayer } from 'components/Seat/SeatWithPlayer';
import { AmountWon } from 'components/Seat/SeatWithPlayer/AmountWon';
import BountyBullets from 'components/Seat/SeatWithPlayer/BountyBullets';
import {
  bountyBadgePositionStyle,
  getAvatarPopStyle,
  getSeatCardStyle,
  opponentNameBase,
  opponentStackBase,
  remToPx,
  seatAvatarScale,
  seatCardInfoStyle
} from 'components/Seat/styles';

import { SeatActionEffects } from './SeatAction';
import { SeatActionBubble } from './SeatActionBubble';

type SeatCardPillProps = {
  seat: any;
  seatId: number;
  currentPlayerSeatId: number;
  isFaceCardUp: boolean;
  isPortrait: boolean;
  hasAction: boolean;
  playerAction: string;
  // Pre-formatted pill label from useSeatLogic (e.g. "STRADDLE (ALL IN)").
  displayAction?: string;
  actionColor: any;
  isWinner: boolean;
  nameSize?: string;
  stackSize?: string;
  cardPosition: any;
  /** Seat's horizontal position on the felt, so the action bubble can open
   *  inward instead of off the side of the screen. */
  seatLeftPct?: number;
  glowRef?: React.RefObject<HTMLDivElement>;
};

export const SeatCardPill = ({
  seat,
  seatId,
  currentPlayerSeatId,
  isFaceCardUp,
  isPortrait,
  hasAction,
  playerAction,
  displayAction,
  actionColor,
  isWinner,
  nameSize,
  stackSize,
  cardPosition,
  seatLeftPct,
  glowRef
}: SeatCardPillProps) => {
  // Shared with the hole-card offsets and the action bubble — see
  // SEAT_AVATAR_SCALE_* for why it is a constant rather than a literal here.
  const avatarScale = seatAvatarScale(isPortrait);
  // Portrait seats are read at arm's length on a phone, so the name/stack need
  // to be larger than landscape. Fall back to the landscape size when a caller
  // passes an explicit size.
  // Beta feedback: seat name/stack bumped ~10% for readability (portrait
  // 0.82/0.8 → 0.9/0.88, landscape 0.65 → 0.72).
  const resolvedNameSize = nameSize ?? (isPortrait ? '0.9rem' : '0.72rem');
  const resolvedStackSize = stackSize ?? (isPortrait ? '0.88rem' : '0.72rem');
  const game = useTableSelector((table) => table.game);
  const displayedStack = useAnimatedWinStack(seatId, useDisplayedStack(seatId, seat.stack));

  // The seat card is a FIXED SEAT_CARD_WIDTH box (see getSeatCardStyle), so a
  // long name or a wide cash balance can no longer widen it.
  //
  // The NAME never truncates. It shrinks until it fits, with no readable floor
  // left to stop it (SEAT_NAME_MIN_PAINTED_PX is 0; SEAT_NAME_ABSOLUTE_MIN_PX
  // only keeps the font size positive) — an ellipsis hides which player you are
  // looking at, which is the one thing this row exists to tell you.
  //
  // The fit MEASURES the rendered name (useShrinkToFit) rather than predicting
  // its width from a character count. Predicting needs the font, the weight and
  // the exact width budget as inputs, and being wrong about any of them clips
  // the name; the element already knows all three. Measuring also makes the
  // Table View Size and `--seat-scale` drop out of this component entirely,
  // since the card and its text scale together.
  //
  // The BALANCE is measured too, for the same reason and one of its own: a
  // truncated money figure is misleading rather than merely shortened
  // ("$1,670…" is equally $1,670.50 and $1,670,000). It used to be sized by the
  // character-count estimate and then re-scaled by minScaledFontSize, which
  // multiplied by `--table-view-scale` and a painted-px floor — growing the text
  // back past the width the estimate had just fitted it to, inside a seat card
  // whose width is fixed and does NOT take that scale. A larger Table View Size
  // therefore forced the ellipsis (reported 2026-09-08). See opponentStackBase.
  const stackText =
    displayedStack !== undefined ? tableMoneyFormatterCompact(displayedStack, game.tabletype) : '';
  const nameRef = useShrinkToFit<HTMLSpanElement>(
    seat.displayName,
    remToPx(resolvedNameSize),
    SEAT_NAME_ABSOLUTE_MIN_PX,
    // START at 10 painted px. Without this a landscape seat, scaled to ~0.56,
    // paints this 11.5px design size at 6.4px — which is what made the name
    // smaller than the balance sitting directly under it.
    SEAT_TEXT_MIN_FONT_PX,
    // …but SHRINK past it rather than holding it and truncating. A name is an
    // identity: "DiamondPlayer99…" no longer tells you who you are playing.
    // Same call the balance row makes one row down. See SEAT_NAME_MIN_PAINTED_PX.
    SEAT_NAME_MIN_PAINTED_PX
  );
  const stackRef = useShrinkToFit<HTMLSpanElement>(
    stackText,
    remToPx(resolvedStackSize),
    SEAT_STACK_MIN_PAINTED_PX,
    // START at 8 painted px…
    SEAT_STACK_MIN_FONT_PX,
    // …but SHRINK past it rather than holding it and truncating. Passing the
    // start size as the floor too made it hard, and ordinary balances
    // ("$1,234,567.89") truncated at small table sizes. Same split the name row
    // above already makes. See SEAT_STACK_MIN_PAINTED_PX.
    SEAT_STACK_MIN_PAINTED_PX
  );

  // Label comes pre-formatted from useSeatLogic ("STRADDLE (ALL IN)" for an
  // all-in straddle); fall back to the raw action for any caller that omits it.
  const resolvedAction = displayAction ?? playerAction.toUpperCase();

  return (
    <div
      ref={glowRef}
      style={{
        ...getSeatCardStyle(isPortrait),
        ...(isWinner
          ? {
              borderWidth: '2px',
              borderStyle: 'solid',
              borderColor: 'rgba(98, 139, 72, 0.8)',
              backgroundColor: 'rgba(98, 139, 72, 0.12)'
            }
          : {}),
        ...(actionColor && !isWinner
          ? {
              backgroundColor: actionColor.bg,
              borderColor: actionColor.border
            }
          : {})
      }}>
      <BountyBullets
        totalBounty={Number(seat.totalBounty) || 0}
        bountyAmount={Number(game.bountyAmount) || 0}
        gameType={game.gameType}
        style={bountyBadgePositionStyle}
      />
      <div style={getAvatarPopStyle(isPortrait)}>
        <div
          style={{
            transform: `scale(${avatarScale})`,
            transformOrigin: 'center center',
            position: 'relative'
          }}>
          {/* Same gate as the bubble below: the flame/particle VFX is the third
              cue for the same last action, so it goes when they go. Unmounting
              (rather than hiding) is deliberate — FlameCanvas runs a
              requestAnimationFrame loop it only cancels on unmount. */}
          {hasAction && <SeatActionEffects action={playerAction} />}
          <SeatWithPlayer
            {...seat}
            currentPlayerSeatId={currentPlayerSeatId}
            seatId={seatId}
            action={playerAction}
            isFaceCardUp={isFaceCardUp}
            cardPosition={cardPosition}
            hideInfo
          />
        </div>
      </div>
      <div style={seatCardInfoStyle}>
        {/* No fontSize here on purpose — useShrinkToFit owns it, and React
            would reset the fitted size on the next stack tick. */}
        <span ref={nameRef} style={opponentNameBase}>
          {seat.displayName}
        </span>
        {/* No fontSize here either — useShrinkToFit owns it (see above). */}
        <span ref={stackRef} style={opponentStackBase}>
          {stackText}
        </span>
      </div>
      {/* Last action — a speech bubble above the portrait, not a row stacked
          under the balance. It is an absolute overlay, so it adds no height to
          the seat card no matter how often the player acts. */}
      {hasAction && (
        <SeatActionBubble
          label={resolvedAction}
          color={actionColor?.border}
          isPortrait={isPortrait}
          // Seats right of the felt's midline open inward; everything else
          // keeps the original rightward bias.
          opensLeft={(seatLeftPct ?? 50) > 50}
        />
      )}
      <AmountWon seatId={seatId} />
    </div>
  );
};
